import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Button, Form, Row, Col, Alert, Spinner } from 'react-bootstrap';
import { FaArrowLeft, FaArrowRight, FaGripVertical } from 'react-icons/fa';

function FieldList({
  title,
  items,
  onMove,
  moveLabel,
  searchTerm,
  setSearchTerm,
  dragIndex,
  setDragIndex,
  onReorder,
}) {
  const filtered = items.filter((f) => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return true;
    return (
      String(f.name).toLowerCase().includes(q) ||
      String(f.label).toLowerCase().includes(q)
    );
  });

  return (
    <>
      <Form.Label>{title}</Form.Label>
      <Form.Control
        type="text"
        placeholder="Search fields..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        className="mb-2"
        size="sm"
      />
      <div
        className="border rounded p-2"
        style={{ maxHeight: 260, overflowY: 'auto' }}
      >
        {filtered.length === 0 ? (
          <div className="text-muted small">No fields</div>
        ) : (
          filtered.map((field, index) => (
            <div
              key={field.name}
              className="d-flex align-items-center justify-content-between py-1 px-1 rounded"
              style={{
                background: dragIndex === index ? '#eef2f8' : 'transparent',
                cursor: onReorder ? 'grab' : 'default',
              }}
              draggable={Boolean(onReorder)}
              onDragStart={() => onReorder && setDragIndex(index)}
              onDragOver={(e) => {
                if (!onReorder) return;
                e.preventDefault();
              }}
              onDrop={() => {
                if (!onReorder || dragIndex == null) return;
                onReorder(dragIndex, index);
                setDragIndex(null);
              }}
              onDragEnd={() => setDragIndex(null)}
            >
              <span className="d-flex align-items-center gap-2 min-w-0">
                {onReorder ? (
                  <FaGripVertical className="text-muted flex-shrink-0" size={12} />
                ) : null}
                <span className="text-truncate" title={field.name}>
                  {field.label || field.name}
                </span>
              </span>
              <Button
                size="sm"
                variant="link"
                className="p-0"
                onClick={() => onMove(field)}
                title={moveLabel}
              >
                {moveLabel === 'Remove' ? <FaArrowLeft /> : <FaArrowRight />}
              </Button>
            </div>
          ))
        )}
      </div>
    </>
  );
}

function reorder(list, from, to) {
  if (from === to) return list;
  const next = [...list];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
}

export default function GlobalSearchFieldsModal({
  show,
  onClose,
  objectConfig,
  onSave,
  saving,
}) {
  const [searchable, setSearchable] = useState([]);
  const [display, setDisplay] = useState([]);
  const [availSearch, setAvailSearch] = useState([]);
  const [availDisplay, setAvailDisplay] = useState([]);
  const [searchTermA, setSearchTermA] = useState('');
  const [searchTermB, setSearchTermB] = useState('');
  const [searchTermC, setSearchTermC] = useState('');
  const [searchTermD, setSearchTermD] = useState('');
  const [dragSearch, setDragSearch] = useState(null);
  const [dragDisplay, setDragDisplay] = useState(null);
  const [error, setError] = useState('');

  const allFields = useMemo(
    () => (objectConfig?.fields || []).map((f) => ({
      name: f.name,
      label: f.label || f.name,
      searchable: f.searchable,
      displayable: f.displayable,
    })),
    [objectConfig]
  );

  useEffect(() => {
    if (!show || !objectConfig) return;
    setError('');
    const selectedSearch = new Set(
      (objectConfig.global_search_fields || []).map((n) => String(n).toLowerCase())
    );
    const selectedDisplay = new Set(
      (objectConfig.global_search_display_fields || []).map((n) =>
        String(n).toLowerCase()
      )
    );
    const searchSel = [];
    (objectConfig.global_search_fields || []).forEach((name) => {
      const f = allFields.find(
        (x) => x.name.toLowerCase() === String(name).toLowerCase()
      );
      if (f && f.searchable) searchSel.push(f);
    });
    const displaySel = [];
    (objectConfig.global_search_display_fields || []).forEach((name) => {
      const f = allFields.find(
        (x) => x.name.toLowerCase() === String(name).toLowerCase()
      );
      if (f && f.displayable) displaySel.push(f);
    });
    setSearchable(searchSel);
    setDisplay(displaySel);
    setAvailSearch(
      allFields.filter((f) => f.searchable && !selectedSearch.has(f.name.toLowerCase()))
    );
    setAvailDisplay(
      allFields.filter((f) => f.displayable && !selectedDisplay.has(f.name.toLowerCase()))
    );
  }, [show, objectConfig, allFields]);

  const handleSave = () => {
    if (searchable.length === 0) {
      setError('Select at least one searchable field');
      return;
    }
    if (display.length === 0) {
      setError('Select at least one display field');
      return;
    }
    onSave({
      global_search_fields: searchable.map((f) => f.name),
      global_search_display_fields: display.map((f) => f.name),
    });
  };

  return (
    <Modal show={show} onHide={onClose} size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>
          Global search fields
          {objectConfig?.object_label ? ` — ${objectConfig.object_label}` : ''}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        {!objectConfig ? (
          <div className="text-center py-4">
            <Spinner animation="border" size="sm" />
          </div>
        ) : (
          <>
            <p className="text-muted small mb-3">
              Searchable fields match the query. Display fields become table columns
              (first column is the record link).
            </p>
            <Row className="mb-4">
              <Col md={6}>
                <FieldList
                  title="Available to search"
                  items={availSearch}
                  searchTerm={searchTermA}
                  setSearchTerm={setSearchTermA}
                  moveLabel="Add"
                  onMove={(field) => {
                    setSearchable((prev) => [...prev, field]);
                    setAvailSearch((prev) => prev.filter((f) => f.name !== field.name));
                  }}
                />
              </Col>
              <Col md={6}>
                <FieldList
                  title="Searchable fields"
                  items={searchable}
                  searchTerm={searchTermB}
                  setSearchTerm={setSearchTermB}
                  moveLabel="Remove"
                  dragIndex={dragSearch}
                  setDragIndex={setDragSearch}
                  onReorder={(from, to) => setSearchable((prev) => reorder(prev, from, to))}
                  onMove={(field) => {
                    setSearchable((prev) => prev.filter((f) => f.name !== field.name));
                    setAvailSearch((prev) => [...prev, field]);
                  }}
                />
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <FieldList
                  title="Available to display"
                  items={availDisplay}
                  searchTerm={searchTermC}
                  setSearchTerm={setSearchTermC}
                  moveLabel="Add"
                  onMove={(field) => {
                    setDisplay((prev) => [...prev, field]);
                    setAvailDisplay((prev) => prev.filter((f) => f.name !== field.name));
                  }}
                />
              </Col>
              <Col md={6}>
                <FieldList
                  title="Display columns (left to right)"
                  items={display}
                  searchTerm={searchTermD}
                  setSearchTerm={setSearchTermD}
                  moveLabel="Remove"
                  dragIndex={dragDisplay}
                  setDragIndex={setDragDisplay}
                  onReorder={(from, to) => setDisplay((prev) => reorder(prev, from, to))}
                  onMove={(field) => {
                    setDisplay((prev) => prev.filter((f) => f.name !== field.name));
                    setAvailDisplay((prev) => [...prev, field]);
                  }}
                />
              </Col>
            </Row>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={saving || !objectConfig}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
